import { useTranslation } from "react-i18next";
import type { CheckResult } from "../../../api/content/reviewDecoders";
import Chip from "../../../ui/feedback/Chip";
import { CHECK_STATUS_TONE } from "./checkStatusTone";
import CheckFindingView from "./CheckFindingView";

/** At most this many findings are listed per check; the review's diff shows where they are. */
const SHOWN_FINDINGS = 5;

const QUESTION_KIND = "competency_question";

/**
 * The results of one check run (FR-MODEL-2 to 5): each check's status, kind, what it checked
 * (a question's text, a shapes item's or test data's name, H-01), its summary, the exact rule
 * set of a reasoning check, and first findings, with how many are shown, listed and found;
 * above them, how many of the questions were checked. The one rendering of a check run, in
 * the review, in the Thread, on the Data model page and in a dataset version's release checks.
 */
export default function CheckResultList({ results }: { results: readonly CheckResult[] }) {
  const { t } = useTranslation();
  const questions = results.filter((result) => result.kind === QUESTION_KIND);
  const notChecked = questions.filter((result) => result.status === "not_checked").length;
  return (
    <div className="space-y-1 text-sm">
      {questions.length > 0 ? (
        <p className="app-text-muted text-xs">
          {t("reviews.checks.questionCoverage", { count: questions.length, checked: questions.length - notChecked, notChecked })}
        </p>
      ) : null}
      <ul className="space-y-1">
        {results.map((result) => {
          // Several focus nodes can violate the same rule; the message-only list groups them.
          const messages = [...new Set(result.findingMessages)];
          const shown = Math.min(messages.length, SHOWN_FINDINGS);
          return (
          <li key={result.checkId} className="flex flex-wrap items-start gap-2">
            <Chip tone={CHECK_STATUS_TONE[result.status]}>{t(`reviews.checks.status.${result.status}`)}</Chip>
            <span className="min-w-0 flex-1">
              <span className="app-text-muted text-xs">{t(`reviews.checks.kinds.${result.kind}`, { defaultValue: result.kind })}</span>{" "}
              {result.title !== null ? <span className="font-medium">{result.title}</span> : null}
              {result.title !== null ? <span className="app-text-muted block text-xs">{result.summary}</span> : result.summary}
              {result.ruleSet !== null ? (
                <span className="app-text-muted block text-xs">
                  {t("reviews.checks.ruleSet", { name: result.ruleSet.name, count: result.ruleSet.rules.length, rules: result.ruleSet.rules.join(", ") })}
                </span>
              ) : null}
              {result.findingMessages.length > 0 ? (
                <ul className="app-text-muted list-disc pl-5 text-xs">
                  {messages.slice(0, SHOWN_FINDINGS).map((message) => (
                    <li key={message}><CheckFindingView finding={{ message }} /></li>
                  ))}
                </ul>
              ) : null}
              {result.findingCount > shown ? (
                <span className="app-text-muted block text-xs">
                  {t("reviews.checks.findingCounts", {
                    shown,
                    listed: result.findingMessages.length,
                    total: result.findingCount,
                  })}
                </span>
              ) : null}
            </span>
          </li>
          );
        })}
      </ul>
    </div>
  );
}
